import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, categoryMap } from '@/mocks/categories';
import { getVideosByCategory } from '@/mocks/videos';
import { getArticlesByCategory } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;
  const categoryVideos = slug ? getVideosByCategory(slug) : [];
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <p className="text-foreground-500 text-lg">分类不存在</p>
          <Link to="/" className="text-primary-400 hover:text-primary-300 mt-4 inline-block">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `51视频 - ${category.name}分类`,
    description: category.description,
    url: `https://www.51video.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '51视频',
      url: 'https://www.51video.com/',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryVideos.map((v, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Movie',
          name: v.title,
          url: `https://www.51video.com/article/${v.id}`,
          image: v.thumbnail,
          description: v.description,
        },
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main className="pt-20">
          {/* Category Header */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
              <div className="mt-6 flex items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-400">
                  <i className={`${category.icon} text-2xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading">
                    {category.name}
                  </h1>
                  <p className="text-foreground-500 text-sm mt-1">{category.description}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Subcategory Tabs */}
          <section className="w-full px-4 md:px-6 lg:px-8 pb-4">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm transition-all whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-950 font-medium'
                        : 'bg-background-900 text-foreground-400 hover:text-foreground-200 hover:bg-background-800 border border-background-800/50'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Videos Grid */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-6 md:py-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg md:text-xl font-bold text-foreground-100 font-heading">
                  共 {categoryVideos.length} 部作品
                </h2>
                <div className="flex items-center gap-2 text-sm text-foreground-500">
                  <span>排序：</span>
                  <select className="bg-background-900 border border-background-800 rounded-lg px-3 py-1.5 text-xs text-foreground-300 outline-none cursor-pointer">
                    <option>最新更新</option>
                    <option>最多观看</option>
                    <option>最高评分</option>
                  </select>
                </div>
              </div>
              {categoryVideos.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {categoryVideos.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-foreground-500">暂无该分类的视频内容</p>
                </div>
              )}
            </div>
          </section>

          {/* Related Articles */}
          {categoryArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14 bg-background-900/50">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-lg md:text-xl font-bold text-foreground-100 font-heading mb-6">
                  相关资讯
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group block p-4 rounded-xl bg-background-900 hover:bg-background-800 border border-background-800/50 transition-all cursor-pointer"
                    >
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full aspect-video rounded-lg object-cover mb-3"
                      />
                      <h3 className="text-foreground-100 text-sm font-medium line-clamp-2 group-hover:text-primary-400 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-foreground-500 text-xs mt-1 line-clamp-2">{article.excerpt}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-foreground-600">
                        <span>{article.publishDate}</span>
                        <span>{article.views} 阅读</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}